import { useState } from 'react';
import { api, ApiError, envPath, keyHead, type IssuedKey, type KeyInfo } from '../api';
import { KeyLine, SecretWarning } from '../components/KeyReveal';
import { ago, ConfirmDialog, Empty, fullDate, PageHeader } from '../components/ui';
import { errorText, useT } from '../i18n';
import { useShell } from './ProjectShell';
import { CLI } from '../cliCommand';
import { EmailUnverifiedNotice } from '../components/EmailUnverified';
import { isEmailUnverified } from '../emailVerify';
import { projectEyebrow } from '../projectNav';

export function KeysPage() {
  const { project, env, reloadProject } = useShell();
  const keys = project.environments.find((e) => e.name === env)?.keys ?? [];
  const [issued, setIssued] = useState<IssuedKey | null>(null);
  const [kind, setKind] = useState<'publishable' | 'secret'>('publishable');
  const [label, setLabel] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  // sk_ の発行が 403 email_unverified（2026-09-28 Core SEC-DEVLOGIN-01）: 赤いエラーではなく「確認メールを開いて」+ 再送のボタン
  const [unverified, setUnverified] = useState(false);
  const [revoke, setRevoke] = useState<KeyInfo | null>(null);
  const [showRevoked, setShowRevoked] = useState(false);
  const { t, tr } = useT();

  const create = async () => {
    setBusy(true);
    setErr(null);
    setUnverified(false);
    try {
      const r = await api<{ key: IssuedKey }>(`${envPath(project.id, env)}/keys`, { method: 'POST', body: { kind, ...(label.trim() ? { label: label.trim() } : {}) } });
      setIssued(r.key);
      setLabel('');
      reloadProject();
    } catch (e) {
      if (isEmailUnverified(e as ApiError)) setUnverified(true);
      else setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  const doRevoke = async () => {
    if (!revoke) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`/v1/projects/${project.id}/keys/${revoke.id}`, { method: 'DELETE' });
      setRevoke(null);
      if (issued?.id === revoke.id) setIssued(null);
      reloadProject();
    } catch (e) {
      setErr(errorText(e as ApiError));
    } finally {
      setBusy(false);
    }
  };

  const visible = keys.filter((k) => showRevoked || !k.revokedAt);
  const revokedCount = keys.filter((k) => k.revokedAt).length;

  return (
    <>
      <PageHeader eyebrow={projectEyebrow(project)} title={t('keys.title')} />

      {issued && (
        <section className="card reveal-card">
          <SecretWarning />
          <KeyLine k={issued} />
          <button type="button" className="btn btn-ghost btn-sm" onClick={() => setIssued(null)}>
            {t('keys.storedHide')}
          </button>
        </section>
      )}

      <section className="card">
        <h2 className="card-title">{t('keys.createTitle', { env: t(`env.inline.${env}`) })}</h2>
        <form
          className="form-row form-inline"
          onSubmit={(e) => {
            e.preventDefault();
            void create();
          }}
        >
          {/* seg-keep-case: 種類の名前に pk_ / sk_ を含む（plain-words-3）。.seg-btn の capitalize だと「Pk_」になるため外す */}
          <fieldset className="seg seg-keep-case" aria-label={t('keys.kindLabel')}>
            {(['publishable', 'secret'] as const).map((k) => (
              <label key={k} className={`seg-btn ${kind === k ? 'is-active' : ''}`}>
                <input type="radio" name="kind" value={k} checked={kind === k} onChange={() => setKind(k)} className="sr-only" />
                {t(`key.kind.${k}`)}
              </label>
            ))}
          </fieldset>
          <label className="field field-grow">
            <span className="sr-only">{t('keys.label')}</span>
            <input value={label} onChange={(e) => setLabel(e.target.value)} placeholder={kind === 'secret' ? t('keys.labelSecret') : t('keys.labelPublishable')} maxLength={60} />
          </label>
          <button className="btn btn-primary" disabled={busy}>
            {busy ? t('keys.creating') : t('keys.create')}
          </button>
        </form>
        <p className="small muted">
          {tr('keys.help', { publishable: <strong>{t('keys.helpPublishable')}</strong>, secret: <strong>{t('keys.helpSecret')}</strong> })}
        </p>
        {unverified && <EmailUnverifiedNotice />}
        {err && (
          <p className="alert alert-error" role="alert">
            {err}
          </p>
        )}
      </section>

      <section className="card">
        <div className="card-head">
          <h2 className="card-title">{t('keys.listTitle')}</h2>
          {revokedCount > 0 && (
            <label className="check small">
              <input type="checkbox" checked={showRevoked} onChange={(e) => setShowRevoked(e.target.checked)} />
              {t('keys.showRevoked', { count: revokedCount })}
            </label>
          )}
        </div>
        {/* 全部失効すると以前は見出しだけの空の表になった。鍵が無いとゲームも CLI も動かないので、次の一手を出す */}
        {visible.length === 0 && (
          <Empty title={t('keys.emptyTitle', { env: t(`env.inline.${env}`) })} icon="lock">
            <p>{tr('keys.emptyBody', { cmd: <code>{CLI} init</code> })}</p>
          </Empty>
        )}
        {visible.length > 0 && (
        <div className="table-wrap">
          <table className="table">
            <thead>
              <tr>
                <th scope="col">{t('keys.col.key')}</th>
                <th scope="col">{t('keys.col.type')}</th>
                <th scope="col" className="hide-sm">{t('keys.col.created')}</th>
                <th scope="col">{t('keys.col.lastUsed')}</th>
                <th scope="col">
                  <span className="sr-only">{t('keys.col.actions')}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {visible.map((k) => (
                <tr key={k.id} className={k.revokedAt ? 'row-revoked' : ''}>
                  <td>
                    <span className="mono">{keyHead(k.prefix)}</span>
                    {k.label && <span className="muted small"> · {k.label}</span>}
                  </td>
                  <td>
                    <span className={`pill ${k.kind === 'secret' ? 'pill-danger' : 'pill-ok'}`}>{t(`key.kind.${k.kind}`)}</span>
                  </td>
                  <td className="hide-sm" title={fullDate(k.createdAt)}>
                    {ago(k.createdAt)}
                  </td>
                  <td>{k.revokedAt ? <span className="muted">{t('keys.revokedAgo', { ago: ago(k.revokedAt) })}</span> : k.lastUsedAt ? ago(k.lastUsedAt) : <span className="muted">{t('common.never')}</span>}</td>
                  <td className="cell-actions">
                    {!k.revokedAt && (
                      <button type="button" className="btn btn-ghost btn-sm btn-danger-text" onClick={() => setRevoke(k)}>
                        {t('keys.revoke')}
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        )}
      </section>

      <ConfirmDialog
        open={!!revoke}
        title={revoke ? t('keys.revokeTitle', { key: keyHead(revoke.prefix) }) : t('keys.revokeTitleGeneric')}
        confirmLabel={t('keys.revokeConfirm')}
        tone="danger"
        busy={busy}
        onClose={() => setRevoke(null)}
        onConfirm={() => void doRevoke()}
      >
        <p>{t(revoke?.kind === 'publishable' ? 'keys.revokeBodyPublishable' : 'keys.revokeBody')}</p>
      </ConfirmDialog>
    </>
  );
}
